import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Users, BookOpen, MapPin, Settings, BarChart3, Bell, Search, Filter, Plus, Download, Upload, Eye, Edit, Trash2, CheckCircle, AlertCircle, UserCheck, Building, GraduationCap, ChevronDown, ChevronRight, Zap, Brain, Target, TrendingUp, Activity, Globe, Cpu, Database, Network, Shield, Star, Award, Lightbulb, Rocket, Sparkles, Bot, ChartLine, PieChart, GitBranch, Layers, Wifi, Cloud, Home } from 'lucide-react';
import TimetableScheduler from './generateTt';

const AdvancedTimetableSystem = () => {
  const [activeTab, setActiveTab] = useState('ai-dashboard');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedSemester, setSelectedSemester] = useState('fall2024');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [generationProgress, setGenerationProgress] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiInsights, setAiInsights] = useState([]);
  const [predictiveAnalytics, setPredictiveAnalytics] = useState({});
  const [realTimeMetrics, setRealTimeMetrics] = useState({});
  const [heatmapData, setHeatmapData] = useState([]);
  const [aiRecommendations, setAiRecommendations] = useState([]);
  const [conflictMatrix, setConflictMatrix] = useState([]);

  // Advanced mock data
  const aiDashboardStats = {
    aiOptimizationScore: 94.7,
    predictedConflicts: 3,
    intelligentRecommendations: 12,
    automationEfficiency: 97.2,
    resourceUtilization: 89.3,
    studentSatisfactionPredict: 91.8,
    facultyWorkloadBalance: 86.4,
    realTimeAdaptations: 247
  };

  const predictiveInsights = [
    { 
      id: 1, 
      type: 'critical', 
      title: 'Predicted Room Shortage', 
      description: 'AI models predict 87% probability of room shortage in Week 12',
      confidence: 87,
      impact: 'high',
      aiSuggestion: 'Recommend hybrid learning for 3 courses',
      timeframe: '8 weeks ahead'
    },
    { 
      id: 2, 
      type: 'optimization', 
      title: 'Faculty Load Imbalance', 
      description: 'Dr. Smith overloaded by 23% while Prof. Johnson underutilized',
      confidence: 94,
      impact: 'medium',
      aiSuggestion: 'Redistribute 2 courses for optimal balance',
      timeframe: 'Next semester'
    },
    { 
      id: 3, 
      type: 'opportunity', 
      title: 'Energy Efficiency Gain', 
      description: 'Consolidating morning classes can reduce energy costs by 31%',
      confidence: 76,
      impact: 'low',
      aiSuggestion: 'Implement smart scheduling algorithm',
      timeframe: 'Immediate'
    }
  ];

  const smartRecommendations = [
    { 
      id: 1, 
      type: 'ai-optimization',
      priority: 'high',
      title: 'Dynamic Room Allocation',
      description: 'AI-powered room swapping based on real-time occupancy',
      potentialSavings: '15% space efficiency',
      implementation: 'Auto-apply',
      status: 'ready'
    },
    { 
      id: 2, 
      type: 'predictive-scheduling',
      priority: 'medium',
      title: 'Weather-Aware Scheduling',
      description: 'Adjust outdoor activities based on weather predictions',
      potentialSavings: '23% cancellation reduction',
      implementation: 'Review & Apply',
      status: 'pending'
    },
    { 
      id: 3, 
      type: 'behavioral-analysis',
      priority: 'high',
      title: 'Student Attendance Patterns',
      description: 'Optimize class timing based on historical attendance data',
      potentialSavings: '18% attendance boost',
      implementation: 'Manual Review',
      status: 'analyzing'
    }
  ];

  // Real-time data simulation
  useEffect(() => {
    const interval = setInterval(() => {
      setRealTimeMetrics({
        activeUsers: Math.floor(Math.random() * 500) + 1200,
        systemLoad: Math.floor(Math.random() * 30) + 40,
        apiCalls: Math.floor(Math.random() * 1000) + 5000,
        responseTime: Math.floor(Math.random() * 20) + 45
      });
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const GlassCard = ({ children, className = "", glow = false }) => (
    <div className={`backdrop-blur-xl bg-white/80 border border-white/20 rounded-2xl shadow-2xl ${glow ? 'shadow-blue-500/20' : ''} ${className}`}>
      {children}
    </div>
  );

  const MetricCard = ({ icon: Icon, title, value, subtitle, trend, color = 'blue', isLive = false }) => (
    <GlassCard className="p-6 hover:scale-105 transition-all duration-300" glow={isLive}>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <Icon className={`h-5 w-5 text-${color}-600`} />
            <span className="text-sm font-medium text-gray-600">{title}</span>
            {isLive && (
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-xs text-green-600 font-medium">LIVE</span>
              </div>
            )}
          </div>
          <div className="space-y-1">
            <p className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
              {value}
            </p>
            {subtitle && (
              <p className="text-sm text-gray-500">{subtitle}</p>
            )}
            {trend && (
              <div className={`flex items-center space-x-1 text-sm ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                <TrendingUp className={`h-3 w-3 ${trend < 0 ? 'rotate-180' : ''}`} />
                <span>{Math.abs(trend)}% {trend > 0 ? 'improvement' : 'decline'}</span>
              </div>
            )}
          </div>
        </div>
        <div className={`p-4 rounded-xl bg-gradient-to-br from-${color}-500 to-${color}-600 shadow-lg`}>
          <Icon className="h-8 w-8 text-white" />
        </div>
      </div>
    </GlassCard>
  );

  const AIInsightCard = ({ insight }) => (
    <GlassCard className={`p-6 border-l-4 ${
      insight.type === 'critical' ? 'border-red-500' :
      insight.type === 'optimization' ? 'border-yellow-500' : 'border-green-500'
    }`}>
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center space-x-3">
          <div className={`p-2 rounded-lg ${
            insight.type === 'critical' ? 'bg-red-100' :
            insight.type === 'optimization' ? 'bg-yellow-100' : 'bg-green-100'
          }`}>
            {insight.type === 'critical' ? 
              <AlertCircle className="h-5 w-5 text-red-600" /> :
              insight.type === 'optimization' ?
              <Target className="h-5 w-5 text-yellow-600" /> :
              <Lightbulb className="h-5 w-5 text-green-600" />
            }
          </div>
          <div>
            <h4 className="font-semibold text-gray-900">{insight.title}</h4>
            <p className="text-sm text-gray-600">{insight.timeframe}</p>
          </div>
        </div>
        <div className="text-right">
          <div className={`px-3 py-1 rounded-full text-xs font-medium ${
            insight.confidence > 90 ? 'bg-green-100 text-green-800' :
            insight.confidence > 75 ? 'bg-yellow-100 text-yellow-800' :
            'bg-red-100 text-red-800'
          }`}>
            {insight.confidence}% confidence
          </div>
        </div>
      </div>
      
      <p className="text-gray-700 mb-4">{insight.description}</p>
      
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
        <div className="flex items-center space-x-2 mb-2">
          <Bot className="h-4 w-4 text-blue-600" />
          <span className="text-sm font-medium text-blue-900">AI Recommendation</span>
        </div>
        <p className="text-sm text-blue-800">{insight.aiSuggestion}</p>
      </div>
    </GlassCard>
  );

  const HeatmapVisualization = () => {
    const timeSlots = ['8:00', '9:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00'];
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
    
    return (
      <GlassCard className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Real-Time Room Utilization Heatmap</h3>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <div className="w-3 h-3 bg-green-400 rounded"></div>
              <span>Low</span>
              <div className="w-3 h-3 bg-yellow-400 rounded"></div>
              <span>Medium</span>
              <div className="w-3 h-3 bg-red-400 rounded"></div>
              <span>High</span>
            </div>
            <div className="flex items-center space-x-1 text-green-600">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-xs font-medium">LIVE DATA</span>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-11 gap-1">
          <div></div>
          {timeSlots.map(time => (
            <div key={time} className="text-xs text-gray-600 text-center p-1">{time}</div>
          ))}
          {days.map(day => (
            <React.Fragment key={day}>
              <div className="text-xs text-gray-600 text-right p-1 font-medium">{day}</div>
              {timeSlots.map(time => {
                const utilization = Math.random();
                return (
                  <div
                    key={`${day}-${time}`}
                    className={`h-8 rounded cursor-pointer hover:scale-110 transition-transform ${
                      utilization > 0.8 ? 'bg-red-400' :
                      utilization > 0.5 ? 'bg-yellow-400' : 'bg-green-400'
                    }`}
                    title={`${day} ${time}: ${Math.round(utilization * 100)}% occupied`}
                  ></div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </GlassCard>
    );
  };

  const SmartRecommendationCard = ({ recommendation }) => (
    <GlassCard className="p-6 hover:shadow-xl transition-all duration-300">
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center space-x-3">
          <div className={`p-2 rounded-lg ${
            recommendation.priority === 'high' ? 'bg-red-100' :
            recommendation.priority === 'medium' ? 'bg-yellow-100' : 'bg-green-100'
          }`}>
            <Sparkles className={`h-5 w-5 ${
              recommendation.priority === 'high' ? 'text-red-600' :
              recommendation.priority === 'medium' ? 'text-yellow-600' : 'text-green-600'
            }`} />
          </div>
          <div>
            <h4 className="font-semibold text-gray-900">{recommendation.title}</h4>
            <span className={`text-xs px-2 py-1 rounded-full ${
              recommendation.priority === 'high' ? 'bg-red-100 text-red-800' :
              recommendation.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
              'bg-green-100 text-green-800'
            }`}>
              {recommendation.priority.toUpperCase()} PRIORITY
            </span>
          </div>
        </div>
        <div className={`px-3 py-1 rounded-full text-xs font-medium ${
          recommendation.status === 'ready' ? 'bg-green-100 text-green-800' :
          recommendation.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
          'bg-blue-100 text-blue-800'
        }`}>
          {recommendation.status.toUpperCase()}
        </div>
      </div>
      
      <p className="text-gray-700 mb-4">{recommendation.description}</p>
      
      <div className="flex justify-between items-center">
        <div className="text-sm">
          <span className="text-gray-600">Potential Impact: </span>
          <span className="font-semibold text-green-600">{recommendation.potentialSavings}</span>
        </div>
        <button className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
          recommendation.implementation === 'Auto-apply' ? 'bg-blue-600 text-white hover:bg-blue-700' :
          recommendation.implementation === 'Review & Apply' ? 'bg-yellow-600 text-white hover:bg-yellow-700' :
          'bg-gray-600 text-white hover:bg-gray-700'
        }`}>
          {recommendation.implementation}
        </button>
      </div>
    </GlassCard>
  );

  const renderAIDashboard = () => (
    <div className="space-y-8">
      {/* Hero Section with Gradient Background */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-800 p-8 text-white">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48Y2lyY2xlIGN4PSIzMCIgY3k9IjMwIiByPSIyIi8+PC9nPjwvZz48L3N2Zz4=')] opacity-20"></div>
        <div className="relative z-10">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2">AI-Powered Academic Intelligence</h1>
              <p className="text-xl opacity-90">Advanced scheduling with machine learning optimization</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="text-3xl font-bold">{aiDashboardStats.aiOptimizationScore}%</div>
                <div className="text-sm opacity-80">AI Optimization Score</div>
              </div>
              <Brain className="h-16 w-16 opacity-80" />
            </div>
          </div>
        </div>
      </div>

      {/* Real-time Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          icon={Cpu}
          title="AI Optimization Score"
          value={`${aiDashboardStats.aiOptimizationScore}%`}
          trend={2.3}
          color="blue"
          isLive={true}
        />
        <MetricCard
          icon={AlertCircle}
          title="Predicted Conflicts"
          value={aiDashboardStats.predictedConflicts}
          subtitle="Next 30 days"
          trend={-15.2}
          color="red"
          isLive={true}
        />
        <MetricCard
          icon={Lightbulb}
          title="Smart Recommendations"
          value={aiDashboardStats.intelligentRecommendations}
          subtitle="Active suggestions"
          color="yellow"
        />
        <MetricCard
          icon={Rocket}
          title="Automation Efficiency"
          value={`${aiDashboardStats.automationEfficiency}%`}
          trend={5.7}
          color="green"
          isLive={true}
        />
      </div>

      {/* Advanced Analytics Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Predictive Insights */}
        <div className="lg:col-span-2 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">Predictive Intelligence</h2>
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <Bot className="h-4 w-4" />
              <span>Powered by Advanced ML</span>
            </div>
          </div>
          
          <div className="space-y-4">
            {predictiveInsights.map(insight => (
              <AIInsightCard key={insight.id} insight={insight} />
            ))}
          </div>
        </div>

        {/* System Performance */}
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-gray-900">System Intelligence</h2>
          
          <GlassCard className="p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Real-Time Performance</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Active Users</span>
                <span className="font-bold text-green-600">{realTimeMetrics.activeUsers || 1247}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">System Load</span>
                <span className="font-bold text-blue-600">{realTimeMetrics.systemLoad || 64}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">API Calls/min</span>
                <span className="font-bold text-purple-600">{realTimeMetrics.apiCalls || 5247}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Response Time</span>
                <span className="font-bold text-orange-600">{realTimeMetrics.responseTime || 52}ms</span>
              </div>
            </div>
          </GlassCard>

          <GlassCard className="p-6">
            <h3 className="font-semibold text-gray-900 mb-4">AI Model Status</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Scheduling Model</span>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <span className="text-xs text-green-600 font-medium">ACTIVE</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Conflict Predictor</span>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <span className="text-xs text-green-600 font-medium">ACTIVE</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Optimization Engine</span>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                  <span className="text-xs text-yellow-600 font-medium">TRAINING</span>
                </div>
              </div>
            </div>
          </GlassCard>
        </div>
      </div>

      {/* Heatmap Visualization */}
      <HeatmapVisualization />

      {/* Smart Recommendations */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Intelligent Recommendations</h2>
          <button className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-lg hover:from-blue-700 hover:to-purple-700 flex items-center space-x-2">
            <Sparkles className="h-4 w-4" />
            <span>Generate New Insights</span>
          </button>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {smartRecommendations.map(recommendation => (
            <SmartRecommendationCard key={recommendation.id} recommendation={recommendation} />
          ))}
        </div>
      </div>
    </div>
  );

  const renderAdvancedGeneration = () => (
    <div className="space-y-8">
      {/* AI Generation Interface */}
      <GlassCard className="p-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Neural Network Timetable Generation</h2>
          <p className="text-gray-600">Advanced AI algorithms with multi-objective optimization</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Academic Period</label>
            <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
              <option>Fall 2024</option>
              <option>Spring 2025</option>
              <option>Summer 2025</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">AI Algorithm</label>
            <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
              <option>Genetic Algorithm</option>
              <option>Simulated Annealing</option>
              <option>Neural Network</option>
              <option>Hybrid Optimization</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Optimization Goals</label>
            <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
              <option>Balanced Multi-Objective</option>
              <option>Maximize Room Utilization</option>
              <option>Minimize Faculty Travel</option>
              <option>Student Preference Priority</option>
            </select>
          </div>
          
          <div className="flex items-end">
            <button
              //onClick={generateTimetable}
              disabled={isGenerating}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 flex items-center justify-center space-x-2"
            >
              {isGenerating ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <Brain className="h-5 w-5" />
                  <span>Generate with AI</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Advanced Progress Visualization */}
        {isGenerating && (
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-xl border border-blue-200">
              <div className="flex justify-between items-center mb-3">
                <span className="text-lg font-semibold text-gray-900">Neural Processing</span>
                <span className="text-lg font-bold text-blue-600">{Math.round(generationProgress)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
                <div 
                  className="bg-gradient-to-r from-blue-500 to-purple-500 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${generationProgress}%` }}
                />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div className="text-center">
                  <div className="font-bold text-blue-600">2,847</div>
                  <div className="text-gray-600">Solutions Evaluated</div>
                </div>
                <div className="text-center">
                  <div className="font-bold text-green-600">94.2%</div>
                  <div className="text-gray-600">Constraint Satisfaction</div>
                </div>
                <div className="text-center">
                  <div className="font-bold text-purple-600">127ms</div>
                  <div className="text-gray-600">Avg. Processing Time</div>
                </div>
                <div className="text-center">
                  <div className="font-bold text-orange-600">3</div>
                  <div className="text-gray-600">Conflicts Remaining</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </GlassCard>

      {/* Advanced Constraints Management */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <GlassCard className="p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">Intelligent Constraints</h3>
          <div className="space-y-4">
            {[
              { name: 'Smart Faculty Preferences', active: true, weight: 0.85, description: 'AI-learned faculty scheduling patterns' },
              { name: 'Dynamic Room Optimization', active: true, weight: 0.92, description: 'Real-time capacity and equipment matching' },
              { name: 'Student Flow Analysis', active: true, weight: 0.78, description: 'Minimize campus traversal time' },
              { name: 'Energy Efficiency Goals', active: false, weight: 0.65, description: 'Optimize building energy usage' },
              { name: 'Weather-Adaptive Scheduling', active: true, weight: 0.71, description: 'Adjust for seasonal patterns' }
            ].map((constraint, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <div className={`w-4 h-4 rounded-full ${constraint.active ? 'bg-green-400' : 'bg-gray-300'}`} />
                    <span className="font-medium text-gray-900">{constraint.name}</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1 ml-7">{constraint.description}</p>
                  {constraint.active && (
                    <div className="ml-7 mt-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-gray-500">Weight: {constraint.weight}</span>
                        <span className="text-xs text-blue-600 font-medium">{Math.round(constraint.weight * 100)}%</span>
                      </div>
                      <div className="w-24 bg-gray-200 rounded-full h-1">
                        <div 
                          className="bg-blue-500 h-1 rounded-full"
                          style={{ width: `${constraint.weight * 100}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <button className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-white">
                  <Settings className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </GlassCard>

        <GlassCard className="p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">Algorithm Performance</h3>
          <div className="space-y-6">
            <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl">
              <div className="text-3xl font-bold text-blue-600 mb-2">94.7%</div>
              <div className="text-sm text-gray-600">Overall Optimization Score</div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">127ms</div>
                <div className="text-xs text-gray-600">Avg Response Time</div>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">2.1M</div>
                <div className="text-xs text-gray-600">Solutions Processed</div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Faculty Satisfaction</span>
                <span className="text-sm font-bold text-green-600">96.2%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Room Utilization</span>
                <span className="text-sm font-bold text-blue-600">89.4%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Conflict Resolution</span>
                <span className="text-sm font-bold text-purple-600">98.7%</span>
              </div>
            </div>
          </div>
        </GlassCard>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'ai-dashboard':
        return renderAIDashboard();
      case 'neural-generation':
        return renderAdvancedGeneration();
      case 'predictive-analytics':
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Predictive Analytics Center</h2>
              <p className="text-gray-600">Advanced forecasting and trend analysis</p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <GlassCard className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Enrollment Predictions</h3>
                <div className="h-64 bg-gradient-to-br from-green-50 to-blue-50 rounded-lg flex items-center justify-center">
                  <div className="text-center">
                    <ChartLine className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500">Advanced trend visualization</p>
                  </div>
                </div>
              </GlassCard>

              <GlassCard className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Resource Demand Forecast</h3>
                <div className="h-64 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg flex items-center justify-center">
                  <div className="text-center">
                    <PieChart className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500">AI-powered predictions</p>
                  </div>
                </div>
              </GlassCard>
            </div>
          </div>
        );
      case 'smart-insights':
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Intelligent Insights Hub</h2>
              <p className="text-gray-600">AI-powered recommendations and actionable insights</p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {smartRecommendations.map(recommendation => (
                <SmartRecommendationCard key={recommendation.id} recommendation={recommendation} />
              ))}
            </div>
          </div>
        );
    case 'optimization':
        return (
          <TimetableScheduler />
        );
      default:
        return renderAIDashboard();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex flex-col"> {/* Added flex flex-col */}

      {/* Advanced Header */}
      <header className="backdrop-blur-xl bg-white/80 border-b border-white/20 shadow-lg z-50 sticky top-0"> {/* Added z-50, sticky, top-0 */}
        <div className="px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="p-3 rounded-xl hover:bg-white/50 transition-colors"
              >
                <div className="w-5 h-5 flex flex-col justify-center space-y-1">
                  <div className="w-full h-0.5 bg-gray-700 rounded"></div>
                  <div className="w-full h-0.5 bg-gray-700 rounded"></div>
                  <div className="w-full h-0.5 bg-gray-700 rounded"></div>
                </div>
              </button>
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl">
                  <Cpu className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    ChronoPech Pro
                  </h1>
                  <p className="text-sm text-gray-600"> Scheduling Platform</p>
                </div>
              </div>
            </div>
             

            <div className="flex items-center space-x-4">
              <button onClick={() => window.location="/ttHome"} className="p-3 text-gray-600 hover:text-gray-900 hover:bg-white/50 rounded-xl relative">
                <Home className="h-5 w-5" />
              </button>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="AI-powered search..."
                  className="pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white/80 backdrop-blur-sm w-80"
                />
              </div>
              <button className="p-3 text-gray-600 hover:text-gray-900 hover:bg-white/50 rounded-xl relative">
                <Bell className="h-5 w-5" />
                <span className="absolute -top-1 -right-1 block h-3 w-3 rounded-full bg-red-400 animate-pulse"></span>
              </button>
              <button className="p-3 text-gray-600 hover:text-gray-900 hover:bg-white/50 rounded-xl">
                <Settings className="h-5 w-5" />
              </button>
              <div className="flex items-center space-x-2 bg-white/50 rounded-xl px-4 py-2">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-green-600">AI Active</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main content area below header, takes remaining height */}
      <div className="flex flex-1"> {/* Added flex-1 */}
        {/* Advanced Sidebar */}
        <aside className={`backdrop-blur-xl bg-white/80 border-r border-white/20 shadow-lg transition-all duration-300 ${
          sidebarCollapsed ? 'w-20' : 'w-72'
        } sticky top-[64px] h-[calc(100vh-64px)] overflow-y-auto z-40`}> {/* Added sticky, top-[header-height], h-screen, overflow-y-auto, z-40 */}
          <nav className="p-6 space-y-3">
            {[
              { id: 'ai-dashboard', label: 'AI Dashboard', icon: Brain, badge: 'LIVE' },
              { id: 'neural-generation', label: 'Neural Generation', icon: Cpu, badge: 'NEW' },
              { id: 'predictive-analytics', label: 'Predictive Analytics', icon: ChartLine },
              { id: 'smart-insights', label: 'Smart Insights', icon: Lightbulb, badge: 12 },
              { id: 'optimization', label: 'Optimization Engine', icon: Target },
              { id: 'real-time', label: 'Real-time Monitor', icon: Activity },
              { id: 'ai-models', label: 'AI Models', icon: Network },
              { id: 'advanced-settings', label: 'Advanced Settings', icon: Settings }
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-left transition-all duration-200 ${
                  activeTab === item.id
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                    : 'text-gray-700 hover:bg-white/60 hover:shadow-md'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <item.icon className="h-5 w-5 flex-shrink-0" />
                  {!sidebarCollapsed && <span className="font-medium">{item.label}</span>}
                </div>
                {!sidebarCollapsed && item.badge && (
                  <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                    typeof item.badge === 'string'
                      ? item.badge === 'LIVE'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-blue-100 text-blue-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {item.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-8 overflow-y-auto h-[calc(100vh-64px)]"> {/* Added overflow-y-auto, flex-1, h-[calc(100vh-64px)] */}
          <div className="max-w-7xl mx-auto">
            {/* Advanced Breadcrumb */}
            <div className="flex items-center space-x-2 text-sm text-gray-600 mb-8">
              <span>Academic Intelligence</span>
              <ChevronRight className="h-4 w-4" />
              <span className="text-gray-900 font-semibold">
                {activeTab.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
              </span>
            </div>

            {renderContent()}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdvancedTimetableSystem;